/*
 * Lemonade Training Oy copy rights all right reserved.
 * Box Access Resolver (F023 / ADR-0002)
 *
 * The one place web, mobile, and Cloud Functions derive "what may this user
 * do at their gym" — not the platform-wide tier system in accessControl.ts.
 *
 * Intended to converge with firestore.rules hasBoxRights / isBoxStaff, but it
 * is currently STRICTER in two places, so do not treat it as a stand-in for
 * the rules when reasoning about what a client can actually read:
 *   - A stale legacy coach flag beside a narrower boxRole grants nothing here;
 *     the rules' isBoxCoach ORs the two arms and still grants coach.
 *   - A known non-active boxStatus denies here; hasBoxRights never reads
 *     boxStatus (only the unused isBoxMember does, and nothing mints it yet).
 * Read those two differences as the RULES being too permissive, not this
 * function being gratuitously strict. The legacy coach pair carries no gym
 * identity, so honouring it beside a narrower boxRole grants staff rights at
 * whatever gym the boxID claim currently points at — including one the person
 * merely bought a membership at. Converging by relaxing this function would
 * import that; converge by tightening the rules, and only after backfilling a
 * boxRole claim for the coaches who have none.
 *
 * The boxStatus difference is the one on a clock: it is inert while nothing
 * mints the claim, and the moment something does, the rules must gain the
 * check in the same release or a suspended coach keeps their Firestore access.
 *
 * Every export here must survive to dist/ as real JavaScript: functions/ is
 * plain JS and consumes these at runtime.
 */

import type { BoxRole } from "./box";
import type { CustomClaims } from "./customClaims";

/**
 * Highest hat wins. Exhaustive on purpose: adding a BoxRole member without a
 * rank here is a compile error rather than a silent ordering gap.
 */
export const BOX_ROLE_RANK: Record<BoxRole, number> = {
  admin: 3,
  coach: 2,
  athlete: 1,
};

/**
 * The scalar the grant routine dual-writes into Members.role, Users.boxRole,
 * and the boxRole claim.
 *
 * Unranked values are skipped rather than compared. Cloud Functions call this
 * from plain JavaScript where the BoxRole parameter type is not enforced, and
 * this codebase's wider role vocabulary carries values the union does not
 * admit ("member"). Comparing an unranked value would silently return it —
 * every comparison against undefined is false, so the first element would win
 * regardless of rank — and writing it into the claim locks the user out at a
 * resolver that correctly refuses to recognise it.
 */
export function highestRole(roles: readonly BoxRole[]): BoxRole | null {
  let best: BoxRole | null = null;
  for (const role of roles) {
    const rank = BOX_ROLE_RANK[role];
    if (rank === undefined) continue;
    if (best === null || rank > BOX_ROLE_RANK[best]) best = role;
  }
  return best;
}

export type BoxAccess = {
  boxID: string | null;
  /** ctx.uid === ctx.ownerUID. Document truth (Box.ownerUID), deliberately independent of claims. */
  isOwner: boolean;
  isBoxAdmin: boolean;
  /** Admin implies coach: true for every admin. */
  isBoxCoach: boolean;
  /** isBoxAdmin || isBoxCoach. */
  isStaff: boolean;
  displayRole: "owner" | "admin" | "coach" | null;
};

export type BoxAccessContext = {
  uid?: string | null;
  ownerUID?: string | null;
};

/** No gym, or no standing at one. Also the correct initial state for a client access store. */
export const NO_BOX_ACCESS: BoxAccess = {
  boxID: null,
  isOwner: false,
  isBoxAdmin: false,
  isBoxCoach: false,
  isStaff: false,
  displayRole: null,
};

/**
 * Pure. Precedence: a PRESENT boxRole claim is authoritative — presence, not
 * validity. The legacy shapes (bare `box` beside a boxID, or `boxCoach` paired
 * with coachType "box") are honoured ONLY when boxRole is absent or null, i.e.
 * for tokens minted before box roles existed. A bare `box` with no boxID is
 * the platform gym licence, which grants no gym access here (see
 * resolveBoxLicense). That precedence is what makes "mint the
 * narrower claims first" actually narrow: a demoted coach whose stale boxCoach
 * flag has not been re-minted yet resolves from boxRole and loses access
 * immediately.
 *
 * A present boxRole outside the BoxRole union grants nothing. Claims arrive as
 * untrusted runtime JSON, and this codebase's wider vocabulary already carries
 * values this union does not admit (a plain gym member is written as
 * "member"), so an unrecognised value must not be mistaken for "no claim" and
 * fall through to the legacy flags — that would let a stale `box` flag
 * out-rank the very claim meant to narrow it.
 *
 * An absent boxStatus means unknown, not inactive — old tokens carry no such
 * key and must not be denied.
 *
 * CALLER CONTRACT: pass the RAW token claims. Never pass a copy that has
 * already coerced an unrecognised boxRole to null, and never drop boxStatus
 * while copying. Both destroy the distinction this function decides on — an
 * unrecognised role rewritten to null is indistinguishable from a legacy
 * token that never had one, so it takes the legacy branch and a stale `box`
 * flag out-ranks the very claim meant to narrow it. A caller that normalises
 * first gets the pre-hardening behaviour back with no test able to see it,
 * which is why BOX_ACCESS_PARITY_CASES must be injected at the caller's
 * parsing input rather than here.
 */
export function resolveBoxAccess(
  claims: Partial<CustomClaims> | null | undefined,
  ctx?: BoxAccessContext,
): BoxAccess {
  const boxID = claims?.boxID ?? null;
  const hasRoleClaim = claims?.boxRole != null;
  const role =
    claims?.boxRole === "admin" ||
    claims?.boxRole === "coach" ||
    claims?.boxRole === "athlete"
      ? claims.boxRole
      : null;
  const legacyPair = claims?.boxCoach === true && claims?.coachType === "box";
  const independentCoach =
    claims?.coach === true && claims?.coachType === "independent";
  const inactive = claims?.boxStatus != null && claims.boxStatus !== "active";

  let isBoxAdmin = false;
  let isBoxCoach = false;
  if (boxID !== null && !inactive) {
    if (hasRoleClaim) {
      isBoxAdmin = role === "admin";
      isBoxCoach = isBoxAdmin || role === "coach";
    } else {
      isBoxAdmin = claims?.box === true && !legacyPair && !independentCoach;
      isBoxCoach = isBoxAdmin || legacyPair;
    }
  }

  const isOwner = !!ctx?.uid && !!ctx?.ownerUID && ctx.uid === ctx.ownerUID;

  return {
    boxID,
    isOwner,
    isBoxAdmin,
    isBoxCoach,
    isStaff: isBoxAdmin || isBoxCoach,
    displayRole: isOwner
      ? "owner"
      : isBoxAdmin
        ? "admin"
        : isBoxCoach
          ? "coach"
          : null,
  };
}

/**
 * Pure. True for the gym-licence token shape: `box: true` and no gym. Decides
 * only whether the holder may create a gym; standing at an existing gym is
 * resolveBoxAccess's job.
 */
export function resolveBoxLicense(
  claims: Partial<CustomClaims> | null | undefined,
): boolean {
  return claims?.box === true && (claims?.boxID ?? null) === null;
}

/** Lane ids shared by web and mobile queries, test ids, and analytics events. */
export const PT_LANE = {
  GYM: "gym",
  MY_PT: "my_pt",
  ALL_PT: "all_pt",
} as const;
export type PtLane = (typeof PT_LANE)[keyof typeof PT_LANE];

/** Every lane query is limit(PROGRAMME_LANE_LIMIT); fixtures in every test tier exceed it. */
export const PROGRAMME_LANE_LIMIT = 50;
